/**
 * The CRM page (DNKADM-0499) — Settings > CRM, Owner/Admin only (the route sits under
 * `RequireAdmin`). One place for how Dink and the tenant's CRM work together, in three
 * tabs routed at `/crm/connection | fields | member-fields` (index → `fields`, the default):
 *  1. **Connection** ({@link CrmConnectionTab}) — connected or not, and the account name.
 *  2. **What Dink keeps** ({@link CrmFieldsTab}) — the Dink-owned fields on every contact,
 *     setting them up, filling in history, and the tracked services.
 *  3. **Fields members can edit** — the Member profile fields screen, moved here unchanged
 *     apart from its title. The old `/member-profile-fields` route redirects to this tab.
 *
 * The active tab comes from the URL, so each is deep-linkable (the `MembershipCreditsScreen`
 * pattern). White-label: the page is named {@link CRM_DISPLAY_NAME}, never the brand.
 */
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { COURT_GREEN } from '@/theme';
import { CRM_DISPLAY_NAME } from '@/utils/whitelabel';

const SUB_TABS = [
  { path: 'connection', label: 'Connection' },
  { path: 'fields', label: 'What Dink keeps on every contact' },
  { path: 'member-fields', label: 'Fields members can edit' },
] as const;

type SubTabPath = (typeof SUB_TABS)[number]['path'];

/** The active tab from the URL; anything else falls back to the default (`fields`). */
function activeTabFrom(pathname: string): SubTabPath {
  const match = SUB_TABS.find(
    (t) => pathname === `/crm/${t.path}` || pathname.startsWith(`/crm/${t.path}/`),
  );
  return match?.path ?? 'fields';
}

export function CrmScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = activeTabFrom(location.pathname);

  return (
    <Box>
      <Typography variant="h1">{CRM_DISPLAY_NAME}</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
        How Dink and your {CRM_DISPLAY_NAME} work together.
      </Typography>

      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_e, value: SubTabPath) => navigate(`/crm/${value}`)}
        aria-label={`${CRM_DISPLAY_NAME} sections`}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTabs-indicator': { bgcolor: COURT_GREEN },
          '& .Mui-selected': { color: `${COURT_GREEN} !important` },
        }}
      >
        {SUB_TABS.map((t) => (
          <Tab key={t.path} value={t.path} label={t.label} />
        ))}
      </Tabs>

      <Box sx={{ pt: 2.5 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
